import { Box, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMemo } from 'react';
import Message from '@sendbird/uikit-react/GroupChannel/components/Message';
import MessageInputWrapper from '@sendbird/uikit-react/GroupChannel/components/MessageInputWrapper';
import { GroupChannel } from '@sendbird/uikit-react/GroupChannel';
import { UserMessage, UserMessageCreateParams } from '@sendbird/chat/message';
import { useAtom, useAtomValue } from 'jotai';
import { Chat_Message_Custom_Type } from '@kheops/utils';
import { Business_Profile_Enum, Contract_Status_Enum } from '@kheops/graphql';
import { currentContextAtom } from '../state';
import { RoutePaths } from '../routes/AppRoutes';
import TopAppBar from '../common/components/TopAppBar';
import { currentChannelAtom, currentContractAtom, showChannelMembersAtom } from './state';
import ConversationActionMessage, { ConversationAction } from './customMessages/ConversationActionMessage';
import AcceptedOfferMessage from './offer/AcceptedOfferMessage';
import OfferMessage from './offer/OfferMessage';
import SupplierInfoMessage from './customMessages/SupplierInfoMessage';
import MakeAnOfferButton from './offer/MakeAnOfferButton';
import ChannelHeader from './ChannelHeader';
import ChannelSeparator from './ChannelSeparator';
import ChannelActions from './ChannelActions';

interface ChannelProps {
  url: string;
  handleBeforeMessageSent: (message: string) => UserMessageCreateParams;
}

export default function Channel({ url, handleBeforeMessageSent }: ChannelProps): React.JSX.Element {
  const { realm } = useAtomValue(currentContextAtom);
  const [currentChannel, setCurrentChannel] = useAtom(currentChannelAtom);
  const currentContract = useAtomValue(currentContractAtom);
  const showChannelMembers = useAtomValue(showChannelMembersAtom);

  const displayMakeAnOfferButton = useMemo((): boolean => {
    return currentContract?.status === Contract_Status_Enum.Unsigned && !currentContract?.proposer_business_profile && !!currentChannel;
  }, [currentContract, currentChannel]);

  const companyName = useMemo((): string => {
    if (!currentChannel || !currentContract) {
      return '';
    }

    return realm === Business_Profile_Enum.Supplier
      ? currentContract.buying_company.tradeName
      : currentContract.supplying_company.tradeName;
  }, [currentContract, currentChannel]);
  
  return (
    <>
      {!showChannelMembers && (
        <TopAppBar
          configuration="small"
          title={companyName}
          backTo={RoutePaths.ROOT}
          startAction={(
            <Button onClick={() => setCurrentChannel(undefined)} size="large">
              <ArrowBackIcon />
            </Button>
          )}
          trailingAction={<ChannelActions channel={currentChannel!} companyName={companyName} />}
          sx={{
            position: {
              xs: 'fixed',
              lg: 'none',
            },
          }}
        />
      )}
      <Box
        className="sendbird-conversation"
        sx={{
          position: 'relative',
          height: {
            xs: 'calc(100% + 44px)',
            lg: '100%',
          },
          '& .sendbird-conversation__footer': {
            position: 'relative',
            pb: 2,
          },
        }}
      >
        <GroupChannel
          channelUrl={url}
          onBeforeSendUserMessage={({ message }) => handleBeforeMessageSent(message)}
          isReactionEnabled={false}
          renderChannelHeader={() => <ChannelHeader />}
          renderCustomSeparator={({ message }) => <ChannelSeparator message={message} />}
          renderMessageInput={() => (
            <>
              {displayMakeAnOfferButton && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    pb: 2,
                  }}
                >
                  <MakeAnOfferButton contract={currentContract!} channel={currentChannel!} />
                </Box>
              )}
              <MessageInputWrapper />
            </>
          )}
          renderMessage={(messageProps) => {
            const { message } = messageProps;

          switch (messageProps.message.customType) {
            case Chat_Message_Custom_Type.SUPPLIER_INFO:
              return (
              <Message {...messageProps}>
                  <SupplierInfoMessage />
              </Message>
              );
            case Chat_Message_Custom_Type.OFFER:
              return (
              <Message {...messageProps}>
                  <OfferMessage message={message as UserMessage} />
              </Message>
              );
            case Chat_Message_Custom_Type.ACCEPTED_OFFER:
              return (
              <Message {...messageProps}>
                  <AcceptedOfferMessage />
              </Message>
              );
            case Chat_Message_Custom_Type.JOINED:
            case Chat_Message_Custom_Type.CREATED:
            case Chat_Message_Custom_Type.LEFT:
            case Chat_Message_Custom_Type.INVITED:
              return (
              <Message {...messageProps}>
                  <ConversationActionMessage message={message as UserMessage} action={message.customType as ConversationAction} />
              </Message>
              );
            default:
              return (
                <Message {...messageProps} />
              );
            }
          }}
        />
      </Box>
    </>
  );
}
